import React, { useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { documentListViewService } from '../../services/document-list-view.service'
import { documentService } from '../../services/document.service'
import { GlobalSearchResult, searchService } from '../../services/search.service'
import { settingsService } from '../../services/settings.service'
import { formatDate } from '../../utils/dateFormatter'
import './GlobalSearch.scss'

// Filter rule type constants (same as React)
const FILTER_FULLTEXT_QUERY = 20
const FILTER_TITLE_CONTENT = 19

enum DataType {
  Document = 'documents',
  SavedView = 'saved_views',
  Tag = 'tags',
  Correspondent = 'correspondents',
  DocumentType = 'document_types',
  StoragePath = 'storage_paths',
  User = 'users',
  Group = 'groups',
  MailAccount = 'mail_accounts',
  MailRule = 'mail_rules',
  CustomField = 'custom_fields',
  Workflow = 'workflows',
}

export const GlobalSearch: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<GlobalSearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [isMac, setIsMac] = useState(false)

  const useAdvancedForFullSearch = settingsService.get(SETTINGS_KEYS.SEARCH_FULL_TYPE) === 'advanced'

  useEffect(() => {
    const checkPlatform = () => {
      const platform = navigator.platform || ''
      const userAgent = navigator.userAgent || ''
      setIsMac(platform.toUpperCase().includes('MAC') || userAgent.toUpperCase().includes('MAC'))
    }
    checkPlatform()
  }, [])

  const shouldIgnoreInput = (activeElement: Element | null): boolean => {
    if (!activeElement) return false

    const tagName = activeElement.tagName.toLowerCase()
    const isInputElement =
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      activeElement.getAttribute('contenteditable') === 'true' ||
      (activeElement as HTMLElement).isContentEditable

    if (isInputElement) {
      return true
    }

    if (activeElement.closest('.modal') || activeElement.closest('.modal-body') || activeElement.closest('.modal-dialog')) {
      return true
    }

    return false
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      const isIgnored = shouldIgnoreInput(activeElement)

      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        if (!isIgnored && activeElement !== searchInputRef.current) {
          e.preventDefault()
          searchInputRef.current?.focus()
          searchInputRef.current?.select()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [])

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSearchResults(null)
      setShowDropdown(false)
      return
    }

    setLoading(true)
    try {
      const results = await searchService.globalSearch(searchQuery.trim())
      setSearchResults(results)
      setShowDropdown(true)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)

    // Debounce search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      handleSearch(value)
    }, 400)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.length > 0) {
      runFullSearch()
      reset(true)
    } else if (e.key === 'Escape') {
      if (query.length > 0) {
        reset(true)
      } else {
        searchInputRef.current?.blur()
      }
    }
  }

  const reset = (close: boolean = false) => {
    setQuery('')
    setSearchResults(null)
    if (close) {
      setShowDropdown(false)
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (query.length > 0) {
      runFullSearch()
    }
  }

  const runFullSearch = () => {
    const ruleType = useAdvancedForFullSearch ? FILTER_FULLTEXT_QUERY : FILTER_TITLE_CONTENT

    documentListViewService.quickFilter([{ rule_type: ruleType, value: query }])

    navigate('/documents')
    reset(true)
  }

  const primaryAction = (type: DataType, item: any) => {
    reset(true)

    switch (type) {
      case DataType.Document:
        navigate(`/documents/${item.id}/details`)
        break
      case DataType.SavedView:
        navigate(`/view/${item.id}`)
        break
      case DataType.Tag:
        navigate(`/documents?tags__id__all=${item.id}`)
        break
      case DataType.Correspondent:
        navigate(`/documents?correspondent__id__in=${item.id}`)
        break
      case DataType.DocumentType:
        navigate(`/documents?document_type__id__in=${item.id}`)
        break
      case DataType.StoragePath:
        navigate(`/documents?storage_path__id__in=${item.id}`)
        break
      default:
        break
    }
  }

  const secondaryAction = async (type: DataType, item: any) => {
    reset(true)

    if (type === DataType.Document) {
      try {
        await documentService.downloadDocumentFile(item.id, false, item.title || item.original_file_name)
      } catch (error) {}
    }
  }

  const getIcon = (type: DataType): string => {
    const iconMap: Record<DataType, string> = {
      [DataType.Document]: 'file-text',
      [DataType.SavedView]: 'funnel',
      [DataType.Tag]: 'tag',
      [DataType.Correspondent]: 'person',
      [DataType.DocumentType]: 'file-earmark',
      [DataType.StoragePath]: 'folder',
      [DataType.User]: 'person-square',
      [DataType.Group]: 'people',
      [DataType.MailAccount]: 'envelope-at',
      [DataType.MailRule]: 'envelope',
      [DataType.CustomField]: 'ui-radios',
      [DataType.Workflow]: 'boxes',
    }
    return iconMap[type] || 'file'
  }

  const renderResultItem = (type: DataType, item: any, nameProp: string, date?: string) => {
    return (
      <div
        key={`${type}-${item.id}`}
        className="dropdown-item py-2 d-flex align-items-center cursor-pointer"
        onClick={() => primaryAction(type, item)}
      >
        <i className={`bi bi-${getIcon(type)} me-2 text-muted`} style={{ width: '1.2em', height: '1.2em' }}></i>
        <div className="text-truncate flex-grow-1">
          {item[nameProp]}
          {date && <small className="small text-muted d-block">{formatDate(date)}</small>}
        </div>
        <div className="btn-group ms-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary d-flex align-items-center"
            onClick={(e) => {
              e.stopPropagation()
              primaryAction(type, item)
            }}
          >
            {type === DataType.Document || type === DataType.SavedView ? (
              <>
                <i className="bi bi-eye"></i>
                <span className="ms-1">{t('open')}</span>
              </>
            ) : (
              <>
                <i className="bi bi-filter"></i>
                <span className="ms-1">{t('filter_documents')}</span>
              </>
            )}
          </button>
          {type === DataType.Document && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center"
              onClick={(e) => {
                e.stopPropagation()
                secondaryAction(type, item)
              }}
            >
              <i className="bi bi-download"></i>
              <span className="ms-1">{t('download')}</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <Dropdown show={showDropdown} onToggle={(show) => setShowDropdown(show)} className="global-search w-100">
      <form className="command-search__form" onSubmit={handleFormSubmit}>
        <span className="command-search__leading">
          <i className="bi bi-search"></i>
        </span>
        <input
          ref={searchInputRef}
          type="text"
          className="command-search__input"
          placeholder={t('search_documents')}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && (
          <span className="command-search__spinner">
            <span className="spinner-border spinner-border-sm"></span>
          </span>
        )}
        <div className="command-search__meta">
          <kbd className="command-search__shortcut">
            {isMac ? (
              <>
                <span>⌘</span> K
              </>
            ) : (
              <>
                <span>Ctrl</span> K
              </>
            )}
          </kbd>
          {query && (
            <button className="command-search__action" type="button" onClick={runFullSearch}>
              {useAdvancedForFullSearch ? t('advanced_search') : t('search')}
              <i className="bi bi-arrow-right-short ms-1"></i>
            </button>
          )}
        </div>
      </form>

      <Dropdown.Menu className="command-search__results shadow-lg w-100">
        {searchResults?.total === 0 && <h6 className="dropdown-header">{t('no_results')}</h6>}

        {searchResults && searchResults.total > 0 && (
          <>
            {searchResults.documents.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('documents')}</h6>
                {searchResults.documents.map((doc) => renderResultItem(DataType.Document, doc, 'title', doc.created))}
              </>
            )}

            {searchResults.saved_views.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('saved_views')}</h6>
                {searchResults.saved_views.map((view) => renderResultItem(DataType.SavedView, view, 'name'))}
              </>
            )}

            {searchResults.tags.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('tags')}</h6>
                {searchResults.tags.map((tag) => renderResultItem(DataType.Tag, tag, 'name'))}
              </>
            )}

            {searchResults.correspondents.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('correspondents')}</h6>
                {searchResults.correspondents.map((correspondent) => renderResultItem(DataType.Correspondent, correspondent, 'name'))}
              </>
            )}

            {searchResults.document_types.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('document_types')}</h6>
                {searchResults.document_types.map((docType) => renderResultItem(DataType.DocumentType, docType, 'name'))}
              </>
            )}

            {searchResults.storage_paths.length > 0 && (
              <>
                <h6 className="dropdown-header">{t('storage_paths')}</h6>
                {searchResults.storage_paths.map((path) => renderResultItem(DataType.StoragePath, path, 'name'))}
              </>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}
